import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface State {
  error: Error | null;
  componentStack: string | null;
}

interface FallbackProps {
  error: Error;
  componentStack: string | null;
  onReset: () => void;
  onReload: () => void;
}

function ErrorBoundaryFallback({ error, componentStack, onReset, onReload }: FallbackProps) {
  const { t, i18n } = useTranslation('errors');
  const isRtl = i18n.dir() === 'rtl';
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-lg w-full rounded-lg border border-danger/40 bg-surface p-6 shadow-elevated">
        <h1 className="text-lg font-bold text-danger mb-2">
          {t('boundary.title', isRtl ? 'حدث خطأ غير متوقع' : 'Something went wrong')}
        </h1>
        <p className="text-sm text-fg-muted mb-4">
          {t(
            'boundary.message',
            isRtl
              ? 'تعذر عرض هذه الشاشة. يمكنك تحديث الصفحة أو العودة إلى الرئيسية.'
              : 'This screen could not be displayed. Try reloading the page or going back home.',
          )}
        </p>
        <pre
          className="bg-bg-muted text-xs rounded p-3 mb-4 max-h-40 overflow-auto whitespace-pre-wrap"
          dir="ltr"
        >
          {error.message}
        </pre>
        {import.meta.env.DEV && componentStack && (
          <details className="text-xs text-fg-muted mb-4">
            <summary className="cursor-pointer">
              {t('boundary.detailsDev', isRtl ? 'تفاصيل (DEV)' : 'Details (DEV)')}
            </summary>
            <pre className="mt-2 bg-bg-muted rounded p-3 max-h-60 overflow-auto" dir="ltr">
              {componentStack}
            </pre>
          </details>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onReset}
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-bg-muted transition-colors"
          >
            {t('boundary.tryAgain', isRtl ? 'محاولة مرة أخرى' : 'Try again')}
          </button>
          <button
            type="button"
            onClick={onReload}
            className="rounded-md bg-brand text-white px-3 py-2 text-sm hover:opacity-90 transition-opacity"
          >
            {t('boundary.reload', isRtl ? 'تحديث الصفحة' : 'Reload page')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Top-level error boundary. Catches uncaught render errors anywhere in the
 * tree below and shows a recovery UI instead of letting React unmount the
 * entire app. Place ABOVE the router so it catches navigation-time errors too.
 */
export class AppErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ error: null, componentStack: null });
  };

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;
    return (
      <ErrorBoundaryFallback
        error={error}
        componentStack={componentStack}
        onReset={this.handleReset}
        onReload={this.handleReload}
      />
    );
  }
}
